/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Select,
  MenuItem,
  FormControl,
  styled,
  useMediaQuery,
  Box,
} from "@mui/material";
import LanguageIcon from "@mui/icons-material/Language";
import AxiosWrapper from "../base/AxiosWrapper";
const Base_URL = process.env.REACT_APP_BE_URL;
const { axiosPutCallWithToken } = AxiosWrapper;
const StyledSelect = styled(Select)(({ theme, hideArrow }) => ({
  fontSize: hideArrow ? "0.75rem" : "0.875rem",
  height: hideArrow ? "1.5rem" : "2rem",
  minWidth: hideArrow ? "70px" : "100px",
  backgroundColor: "transparent",
  color: theme.palette.text.primary,
  borderRadius: 8,
  padding: hideArrow ? "0 2px" : undefined,
  ".MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    border: "1px solid",
    borderColor: theme.palette.primary.main,
  },
  ...(hideArrow && {
    "& .MuiSelect-icon": {
      display: "none",
    },
    "& .MuiSelect-select": {
      paddingRight: "2px !important",
      paddingLeft: "2px !important",
    },
    "& .MuiInputBase-input": {
      paddingRight: "2px !important",
      paddingLeft: "2px !important",
    },
  }),
}));

const LanguageSelector = () => {
  const { i18n } = useTranslation();
  const [availableLanguages, setAvailableLanguages] = useState([]);
  const [currentLanguage, setCurrentLanguage] = useState("en");

  const allLanguages = {
    en: "English",
    fr: "Français",
    es: "Español",
    ar: "Arabic",
    fil: "Filipino",
    tpi: "Tok Pisin",
    pt: "Portuguese",
    id: "Indonesian",
  };
  const isGalaxyFold = useMediaQuery("(max-width: 344px)");
  const updateLanguageAPI = async (languageCode) => {
    const token = localStorage.getItem("token");
    if (!token) {
      return;
    }
    try {
      const response = await axiosPutCallWithToken(
        `${Base_URL}/users/languageUpdate?update=${languageCode}`
      );
      console.log("Language updated successfully", response.data);
    } catch (error) {
      console.error(
        "Failed to update language:",
        error.response?.data || error
      );
    }
  };

  useEffect(() => {
    const updateLanguages = () => {
      const token = localStorage.getItem("token");
      const languagesFromStorage =
        localStorage.getItem("language")?.split(",") || [];
      const defaultLanguageFromStorage =
        localStorage.getItem("is_language") || "en";

      if (!token) {
        const all = Object.entries(allLanguages).map(([code, name]) => ({
          code,
          name,
        }));
        setAvailableLanguages(all);
        setCurrentLanguage("en");
        i18n.changeLanguage("en");
      } else {
        if (
          languagesFromStorage.length === 0 ||
          languagesFromStorage[0] === ""
        ) {
          const all = Object.entries(allLanguages).map(([code, name]) => ({
            code,
            name,
          }));
          setAvailableLanguages(all);
          setCurrentLanguage(defaultLanguageFromStorage || "en");
          i18n.changeLanguage(defaultLanguageFromStorage || "en");
        } else {
          const filteredLanguages = languagesFromStorage
            .filter((code) => allLanguages[code])
            .map((code) => ({ code, name: allLanguages[code] }));

          setAvailableLanguages(filteredLanguages);

          const fallbackLang = filteredLanguages.find(
            (lang) => lang.code === defaultLanguageFromStorage
          )
            ? defaultLanguageFromStorage
            : filteredLanguages[0]?.code || "en";

          setCurrentLanguage(fallbackLang);
          i18n.changeLanguage(fallbackLang);
        }
      }
    };
    updateLanguages();
    const handleStorageChange = (e) => {
      if (
        e.key === "token" ||
        e.key === "language" ||
        e.key === "is_language" ||
        e.type === "cStorage"
      ) {
        updateLanguages();
      }
    };

    const handleCustomStorageChange = () => {
      updateLanguages();
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("cStorage", handleCustomStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("cStorage", handleCustomStorageChange);
    };
  }, [i18n]);

  const handleChange = async (event) => {
    const selectedLanguage = event.target.value;
    const token = localStorage.getItem("token");
    setCurrentLanguage(selectedLanguage);
    i18n.changeLanguage(selectedLanguage);
    localStorage.setItem("is_language", selectedLanguage);
    if (token) {
      await updateLanguageAPI(selectedLanguage);
    }
  };

  return (
    <FormControl variant="outlined" size="small">
      <StyledSelect
        value={currentLanguage}
        onChange={handleChange}
        hideArrow={isGalaxyFold}
        displayEmpty
        inputProps={{ "aria-label": "Select language" }}
        sx={{ fontSize: isGalaxyFold ? ".75rem" : ".875rem" }}
        startAdornment={
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mr: isGalaxyFold ? 0.2 : 0.5,
            }}
          >
            <LanguageIcon
              sx={{
                fontSize: isGalaxyFold ? "0.8rem" : "1rem",
                color: "text.secondary",
              }}
            />
          </Box>
        }
      >
        {availableLanguages.map((language) => (
          <MenuItem
            key={language.code}
            value={language.code}
            sx={{
              fontSize: isGalaxyFold ? ".75rem" : ".875rem",
              ...(isGalaxyFold && { padding: "2px 2px", minHeight: "auto" }),
            }}
          >
            {language.name}
          </MenuItem>
        ))}
      </StyledSelect>
    </FormControl>
  );
};

export default LanguageSelector;
